// File: src/pages/Gallery.jsx
// Description: Gallery page — album card grid layout.
// Click an album card to open a full album viewer with lightbox.

import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/pages/gallery.css';

const BASE_URL = 'https://api.mahavihara-international.org';

// ── Bilingual labels ──────────────────────────────────────────────────────────
const LABELS = {
  en: {
    pageTitle: 'Gallery',
    pageSubtitle: 'Moments captured through our journey',
    latest: 'Latest First',
    earliest: 'Earliest First',
    noData: 'No gallery albums found.',
    loading: 'Loading gallery...',
    error: 'Failed to load gallery. Please try again.',
    retry: 'Retry',
    photos: 'photos',
    backToAlbums: '← Back to Albums',
  },
  mm: {
    pageTitle: 'ဓာတ်ပုံများ',
    pageSubtitle: 'ကျွန်ုပ်တို့၏ မှတ်တမ်းများ',
    latest: 'နောက်ဆုံးအရင်',
    earliest: 'အစောဆုံးအရင်',
    noData: 'ဓာတ်ပုံ အယ်လ်ဘမ်များ မတွေ့ပါ။',
    loading: 'ဓာတ်ပုံများ ဖွင့်နေသည်...',
    error: 'ဓာတ်ပုံများ ဖွင့်မရပါ။ ထပ်မံကြိုးစားပါ။',
    retry: 'ထပ်ကြိုးစားရန်',
    photos: 'ဓာတ်ပုံ',
    backToAlbums: '← အယ်လ်ဘမ်များသို့ ပြန်သွားရန်',
  },
};

const SORT = { LATEST: 'latest', EARLIEST: 'earliest' };

// ── Helpers ───────────────────────────────────────────────────────────────────
const resolveUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BASE_URL}/${path}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// ── Lightbox — full-screen single photo viewer ────────────────────────────────
const Lightbox = ({ photos, startIndex, onClose }) => {
  const [current, setCurrent] = useState(startIndex);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') setCurrent(c => (c + 1) % photos.length);
      if (e.key === 'ArrowLeft')  setCurrent(c => (c - 1 + photos.length) % photos.length);
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [photos.length, onClose]);

  return (
    <div className="glightbox-backdrop" onClick={onClose}>
      <div className="glightbox-box animate-zoom-in" onClick={e => e.stopPropagation()}>
        <button className="glightbox-close" onClick={onClose}>✕</button>

        <img
          src={resolveUrl(photos[current].photo_path)}
          alt={`photo-${current + 1}`}
          className="glightbox-img"
        />

        {photos.length > 1 && (
          <>
            <button
              className="glightbox-arrow glightbox-arrow--prev"
              onClick={e => { e.stopPropagation(); setCurrent(c => (c - 1 + photos.length) % photos.length); }}
            >‹</button>
            <button
              className="glightbox-arrow glightbox-arrow--next"
              onClick={e => { e.stopPropagation(); setCurrent(c => (c + 1) % photos.length); }}
            >›</button>
          </>
        )}

        <div className="glightbox-counter">{current + 1} / {photos.length}</div>

        {photos.length > 1 && (
          <div className="glightbox-thumbs">
            {photos.map((p, i) => (
              <img
                key={p.id}
                src={resolveUrl(p.photo_path)}
                alt={`thumb-${i}`}
                className={`glightbox-thumb ${i === current ? 'active' : ''}`}
                onClick={() => setCurrent(i)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Album Viewer — full page showing all photos of one album ──────────────────
const AlbumViewer = ({ album, lang, labels, onBack }) => {
  const [lightbox, setLightbox] = useState(null); // index | null
  const title  = lang === 'mm' ? album.title_mm : album.title_en;
  const photos = album.photos || [];

  return (
    <div className="gallery-album-viewer animate-fade-in-down">
      {/* Back button + album title */}
      <div className="gav-header container">
        <button className={`gav-back ${lang === 'mm' ? 'font-myanmar' : ''}`} onClick={onBack}>
          {labels.backToAlbums}
        </button>
        <div className="gav-meta">
          <h2 className={`gav-title ${lang === 'mm' ? 'font-myanmar' : ''}`}>{title}</h2>
          <span className="gav-count">📷 {photos.length} {labels.photos}</span>
          <span className="gav-date">{formatDate(album.created_at)}</span>
        </div>
      </div>

      {/* Masonry-style photo grid */}
      <div className="gav-grid container">
        {photos.map((photo, i) => (
          <div
            key={photo.id}
            className="gav-photo-wrap"
            onClick={() => setLightbox(i)}
          >
            <img
              src={resolveUrl(photo.photo_path)}
              alt={`${title} ${i + 1}`}
              loading="lazy"
              className="gav-photo"
            />
            <div className="gav-photo-overlay">
              <span className="gav-zoom-icon">🔍</span>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <Lightbox
          photos={photos}
          startIndex={lightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
};

// ── Album Card ────────────────────────────────────────────────────────────────
const AlbumCard = ({ album, lang, labels, onClick }) => {
  const title      = lang === 'mm' ? album.title_mm : album.title_en;
  const coverPhoto = album.photos?.[0];
  const count      = album.photos?.length || 0;

  return (
    <div className="gal-card scroll-fade" onClick={onClick}>
      {/* Cover image */}
      <div className="gal-card__cover">
        {coverPhoto ? (
          <img
            src={resolveUrl(coverPhoto.photo_path)}
            alt={title}
            loading="lazy"
            className="gal-card__img"
          />
        ) : (
          <div className="gal-card__no-photo">📷</div>
        )}

        {/* Photo count badge */}
        <div className="gal-card__badge">{count} {labels.photos}</div>

        {/* Hover overlay */}
        <div className="gal-card__overlay">
          <span className="gal-card__view-icon">👁</span>
        </div>
      </div>

      {/* Card footer */}
      <div className="gal-card__footer">
        <h3 className={`gal-card__title ${lang === 'mm' ? 'font-myanmar' : ''}`}>
          {title}
        </h3>
        <span className="gal-card__date">{formatDate(album.created_at)}</span>
      </div>
    </div>
  );
};

// ── Main Gallery Page ─────────────────────────────────────────────────────────
const Gallery = () => {
  const { currentLanguage } = useLanguage();
  const lang   = currentLanguage === 'my' ? 'mm' : 'en';
  const labels = LABELS[lang];

  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [sort, setSort]           = useState(SORT.LATEST);
  const [openAlbum, setOpenAlbum] = useState(null); // album object | null

  // ── Fetch ──
  const fetchGalleries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(`${BASE_URL}/gallery`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setGalleries(data.data || data);
    } catch {
      setError(labels.error);
    } finally {
      setLoading(false);
    }
  }, [labels.error]);

  useEffect(() => { fetchGalleries(); }, [fetchGalleries]);

  // ── Scroll-fade observer ──
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e =>
        e.target.classList.toggle('visible', e.isIntersecting)
      ),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.scroll-fade').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [galleries, openAlbum]);

  // ── Sort ──
  const sorted = [...galleries].sort((a, b) => {
    const da = new Date(a.created_at), db = new Date(b.created_at);
    return sort === SORT.LATEST ? db - da : da - db;
  });

  // ── If an album is open, show AlbumViewer ──
  if (openAlbum) {
    return (
      <AlbumViewer
        album={openAlbum}
        lang={lang}
        labels={labels}
        onBack={() => setOpenAlbum(null)}
      />
    );
  }

  return (
    <div className="gallery-page">

      {/* Banner */}
      <div className="page-banner">
        <div className="container">
          <div className="banner-content">
            <h1 className={`banner-title ${lang === 'mm' ? 'font-myanmar' : ''}`}>
              {labels.pageTitle}
            </h1>
            <p className={`banner-subtitle ${lang === 'mm' ? 'font-myanmar' : ''}`}>
              {labels.pageSubtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Sort controls */}
      <div className="gallery-controls container">
        <div className="gallery-sort">
          <button
            className={`gallery-sort-btn ${sort === SORT.LATEST ? 'active' : ''}`}
            onClick={() => setSort(SORT.LATEST)}
          >
            🕐 <span className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.latest}</span>
          </button>
          <button
            className={`gallery-sort-btn ${sort === SORT.EARLIEST ? 'active' : ''}`}
            onClick={() => setSort(SORT.EARLIEST)}
          >
            🕰 <span className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.earliest}</span>
          </button>
        </div>
      </div>

      {/* Album grid */}
      <section className="gallery-section section-padding">
        <div className="container">

          {loading && (
            <div className="gallery-state text-center">
              <div className="spinner-ring" />
              <p className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.loading}</p>
            </div>
          )}

          {error && !loading && (
            <div className="gallery-state gallery-state--error text-center">
              <p>{error}</p>
              <button className="btn-primary" onClick={fetchGalleries}>
                {labels.retry}
              </button>
            </div>
          )}

          {!loading && !error && sorted.length === 0 && (
            <div className="gallery-state text-center">
              <p className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.noData}</p>
            </div>
          )}

          {!loading && !error && sorted.length > 0 && (
            <div className="gallery-grid">
              {sorted.map(album => (
                <AlbumCard
                  key={album.id}
                  album={album}
                  lang={lang}
                  labels={labels}
                  onClick={() => setOpenAlbum(album)}
                />
              ))}
            </div>
          )}

        </div>
      </section>
    </div>
  );
};

export default Gallery;